import {
  CreatePersonRequest,
  isTeamMember,
  Person,
  PersonPermission,
  TeamPermission,
  TeamRole
} from 'nekst-api';
import { useAuthorizationChecker } from 'authorization-scope';
import { PhoneNumbersInput, Separator, TimezoneDropdown } from 'features/nekst-widgets';
import {
  CheckboxInput,
  FormBlock,
  IfFormData,
  ifReactNative,
  InputLayoutIcon,
  isReactNative,
  RelativeRow,
  TextArea,
  TextInput,
  Wysiwyg
} from 'ui-builder';
import React from 'react';
import { AvatarInput, CalendarLinksBlock } from './Shared';
import { AddressInput, ServiceProvidersInput, TeamRoleRadio } from '../Shared';
import { AutoAssignRolesToggleWrapper, EventTasksToggleWrapper } from 'feature-toggle';
import { AutoAssignRolesInput } from './AutoAssignRolesInput';
import { SignatureHtmlPreview } from './SignatureHtmlPreview';
import signatureStyles from './SignatureInput.module.scss';

export function UpdatePersonFormFields(
  props: {
    person: Person,
  },
) {
  const { isGranted } = useAuthorizationChecker();
  const canManageTeamRole = isGranted(TeamPermission.HAS_TEAM)
    && isGranted(TeamPermission.CREATE_TEAM_MEMBER)
    && [TeamRole.MEMBER, TeamRole.ADMINISTRATOR].includes(props.person.teamRole);

  return (
    <FormBlock>
      {isGranted(PersonPermission.UPDATE_AVATAR) && (
        <>
          <RelativeRow weights={ifReactNative([12], [6, 6])}>
            <AvatarInput person={props.person} />
            {isGranted(PersonPermission.UPDATE) && (
              <CalendarLinksBlock person={props.person} />
            )}
          </RelativeRow>
          <Separator />
        </>
      )}
      <RelativeRow weights={ifReactNative([12], [6, 6])}>
        <TextInput
          source="profile.name.firstName"
          label="First Name"
          icon={InputLayoutIcon.PERSON}
        />
        <TextInput
          source="profile.name.lastName"
          label="Last name"
          icon={InputLayoutIcon.PERSON}
        />
      </RelativeRow>
      <RelativeRow weights={ifReactNative([12], [6, 6])}>
        <TextInput
          source="profile.primaryEmail"
          label="Primary Email"
          isDisabled={
            [
              TeamRole.ADMINISTRATOR,
              TeamRole.OWNER,
              TeamRole.MEMBER,
            ].includes(props.person.teamRole)
          }
          icon={InputLayoutIcon.EMAIL}
        />
        <TextInput
          source="profile.alternateEmail"
          label="Alternate Email"
          icon={InputLayoutIcon.EMAIL}
        />
      </RelativeRow>
      <RelativeRow weights={ifReactNative([12], [6, 6])}>
        <TextInput
          source="profile.companyName"
          label="Company Name"
          icon={InputLayoutIcon.WORK}
        />
        <TextInput
          source="profile.website"
          label="Website"
          icon={InputLayoutIcon.WEB}
        />
      </RelativeRow>
      <PhoneNumbersInput
        source="profile.phoneNumbers"
        label="Phone Numbers"
        openCreateForm
      />
      <Separator />
      <AddressInput source="profile.address" />
      {!isReactNative() && (
        <>
          <Separator />
          <CheckboxInput
            source="profile.usePlainHtmlSignature"
            label="Use plain HTML for signature"
          />
          <IfFormData<Person>
            conditionFunc={(data) => !!data.profile?.usePlainHtmlSignature}
          >
            <TextArea source="profile.emailSignature" label="Custom Email Signature" className={signatureStyles.signatureInput} />
            <SignatureHtmlPreview />
          </IfFormData>
          <IfFormData<Person>
            conditionFunc={(data) => !data.profile?.usePlainHtmlSignature}
          >
            <Wysiwyg source="profile.emailSignature" label="Custom Email Signature" maxImageWidth={1200} />
          </IfFormData>
        </>
      )}
      {canManageTeamRole && (
        <>
          <Separator />
          <TeamRoleRadio source="teamRole" label="" showContactOption={false} />
        </>
      )}
      <IfFormData<CreatePersonRequest>
        conditionFunc={(data) => data.teamRole === TeamRole.CONTACT}
        onUnmount={(callback) => {
          callback({
            serviceProvidersIds: undefined,
          });
        }}
      >
        <Separator />
        <ServiceProvidersInput source="serviceProvidersIds" label="Service Providers" />
      </IfFormData>
      {isTeamMember(props.person.teamRole) && (
        <EventTasksToggleWrapper>
          <Separator />
          <TimezoneDropdown source="profile.timezone" label="Timezone" />
        </EventTasksToggleWrapper>
      )}
      <AutoAssignRolesToggleWrapper>
        <AutoAssignRolesInput person={props.person} />
      </AutoAssignRolesToggleWrapper>
    </FormBlock>
  );
}
